import React, { useCallback, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import CheckCircleIcon from '@atlaskit/icon/core/check-circle';
import StatusWarningIcon from '@atlaskit/icon/core/status-warning';
import StatusWorkflowReturnedIcon from '@atlaskit/icon-lab/core/status-workflow-returned';
import { token } from '@atlaskit/tokens';

import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';
import { expValEquals, UNSAFE_expValEqualsNoExposure } from '@confluence/feature-experiments';
import { EDIT_PAGE_V2, VIEW_PAGE } from '@confluence/named-routes/entry-points/routes.ts';
import { usePageState } from '@confluence/page-context';
import { useRightSidebarContext } from '@confluence/page-layout-context';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { useApprovals } from '../ApprovalsContext.tsx';
import { openApprovalsPaneForContext } from '../approvalsPaneSidebar.ts';
import { setCrossModeApprovalRespondIntent } from '../crossModeApprovalRespondIntent.ts';

import {
	addDismissedCompletionReviewId,
	getDismissedCompletionReviewIds,
} from '../approvalCompletionDismissals.ts';
import { getNoonUtcDateFromDateOnly } from '../dueDateUtils.ts';

import {
	BannerComponent,
	type BannerAvatarProps,
	type ProfileCardMode,
} from './BannerComponent.tsx';

const i18n = defineMessages({
	approvalInProgressHeading: {
		id: 'approvals.banner.approval-in-progress.heading',
		defaultMessage: 'Approval in progress',
		description: 'Heading shown in the approval banner when a review is in progress',
	},
	approvedDescription: {
		id: 'approvals.banner.approved.description',
		defaultMessage: '{current}/{total} {current, plural, one {approved} other {approved}}',
		description:
			'Description showing how many approvers have approved out of the total when there are no denials, e.g. "2/5 approved"',
	},
	respondedDescription: {
		id: 'approvals.banner.responded.description',
		defaultMessage: '{current}/{total} {current, plural, one {responded} other {responded}}',
		description:
			'Description showing how many approvers have responded out of the total when there are denials, e.g. "3/5 responded"',
	},
	yourApprovalNeededHeading: {
		id: 'approvals.banner.your-approval-needed.heading',
		defaultMessage: 'Your approval is needed',
		description: 'Heading shown when the current user needs to provide their approval',
	},
	yourApprovalNeededByDueDateHeading: {
		id: 'approvals.banner.your-approval-needed-by-due-date.heading.ai-non-final',
		defaultMessage: 'Your approval is needed by {dueDate}',
		description:
			'Heading shown in the approval banner when the current user needs to provide approval by a due date. The {dueDate} placeholder is the formatted approval due date.',
	},
	approverRequestFromDescription: {
		id: 'approvals.banner.approver-request-from.description',
		defaultMessage: 'From {requestorName}',
		description:
			'Second line of an approval request banner when the requester left no message, naming who requested the approval, e.g. "From John"',
	},
	approverRequestDescription: {
		id: 'approvals.banner.approver-request.description',
		// oxlint-disable-next-line eslint-core/no-restricted-syntax
		defaultMessage: 'From {requestorName}: "{reviewComment}"',
		description:
			'Description showing who requested the approval and their message, e.g. From John: "Please review this page"',
	},
	respondCta: {
		id: 'approvals.banner.respond.cta',
		defaultMessage: 'Respond',
		description: 'Button label for an approver who has not yet responded to the approval request',
	},
	editResponseCta: {
		id: 'approvals.banner.edit-response.cta',
		defaultMessage: 'Edit response',
		description:
			'Button label for an approver who has already responded and wants to change their response',
	},
	viewDetailsCta: {
		id: 'approvals.banner.view-details.cta',
		defaultMessage: 'View details',
		description:
			'Button label for viewers and requestors to view the approval details in the side panel',
	},
	blockedApprovalHeading: {
		id: 'approvals.banner.blocked-approval.heading',
		defaultMessage: 'This approval needs attention.',
		description:
			'Heading shown to requesters and approvers when an approval is blocked because required approvers changed.',
	},
	seeDetailsCta: {
		id: 'approvals.banner.see-details.cta',
		defaultMessage: 'See details',
		description:
			'Button label shown to the requester to open details for a blocked approval requiring attention.',
	},
	yourApprovalNeededOnUnpublishedChangesHeading: {
		id: 'approvals.banner.your-approval-needed-on-unpublished-changes.heading',
		defaultMessage: 'Your approval is needed on unpublished changes',
		description:
			'Heading shown on the published page when the current user needs to approve the draft (unpublished changes) of the page',
	},
	yourApprovalNeededOnPublishedPageHeading: {
		id: 'approvals.banner.your-approval-needed-on-published-page.heading',
		defaultMessage: 'Your approval is needed on the published page',
		description:
			'Heading shown in the editor when the current user needs to approve the published version of the page',
	},
	pageApprovedHeading: {
		id: 'approvals.banner.page-approved.heading',
		defaultMessage: 'Page approved',
		description:
			'Heading shown to the requester in the approval banner after every approver approved the page',
	},
	changesRequestedHeading: {
		id: 'approvals.banner.changes-requested.heading',
		defaultMessage: 'Changes requested',
		description:
			'Heading shown to the requester in the approval banner after the review completed with at least one approver requesting changes',
	},
	dismissBannerLabel: {
		id: 'approvals.banner.dismiss.label',
		defaultMessage: 'Dismiss',
		description: 'Accessible label for the close (X) button that hides the approval banner',
	},
});

const getProfileCardMode = (hasProfileCardSurface: boolean): ProfileCardMode | undefined =>
	hasProfileCardSurface ? 'click' : undefined;

/**
 * ApprovalBanner - Smart wrapper around BannerComponent that consumes ApprovalsContext
 *
 * Renders the appropriate approval banner based on the current user's role:
 * - Approver who hasn't responded: "Your approval is needed" with "Respond" CTA
 * - Approver who has responded: "Approval in progress" with "Edit response" CTA
 * - Requestor / Viewer: "Approval in progress" with "View details" CTA
 *
 * When this mode has no pending review but the current user's approval is pending on the other
 * mode (confluence_approvals_banner_updates_exp), renders a dismissible cross-mode banner whose
 * "Respond" CTA navigates to that mode and opens the approvals pane there. Otherwise, once this
 * mode's review is APPROVED or DENIED, the requester sees a dismissible end-state banner ("Page
 * approved" / "Changes requested"); dismissals are remembered per review.
 *
 * Renders nothing if:
 * - Data is still loading
 * - There is no active review and no cross-mode request
 * - The review is not in PENDING status and there is no cross-mode request
 */
export const ApprovalBanner: React.FC = () => {
	const { formatDate, formatMessage } = useIntl();
	const { edition } = useSessionData();
	const {
		approvalRequest,
		approvalRequestLoading,
		currentUserApprover,
		contentId,
		contentStatus,
		isContentApprovalsEnabled,
		isCurrentUserRequestor,
		isPendingReviewBlockedByRequiredApproversChange,
		otherModeReview,
		otherModeCurrentUserApprover,
	} = useApprovals();
	const rightSidebarContext = useRightSidebarContext();

	const openApprovalsPane = useCallback(() => {
		openApprovalsPaneForContext({
			contentId,
			contentStatus,
			rightSidebarContext,
		});
	}, [contentId, contentStatus, rightSidebarContext]);

	const approvers = approvalRequest?.review?.approvers;

	const avatarData = useMemo((): BannerAvatarProps[] => {
		if (!approvers?.length) return [];

		// Sort approvers by status, so that approvers that denied are shown first
		const statusOrder: Record<string, number> = {
			REVIEWER_DENIED: 0,
			REVIEWER_APPROVED: 1,
		};
		const defaultOrder = 2; // PENDING_REVIEW and any other statuses come last

		const sorted = [...approvers].sort(
			(a, b) => (statusOrder[a.status] ?? defaultOrder) - (statusOrder[b.status] ?? defaultOrder),
		);

		// Convert approvers to AvatarProps for a presentational AvatarGroup
		return sorted.map((approver): BannerAvatarProps => {
			let status: BannerAvatarProps['status'];
			if (approver.status === 'REVIEWER_APPROVED') {
				status = 'approved';
			} else if (approver.status === 'REVIEWER_DENIED') {
				status = 'declined';
			}

			const userId = approver.principalType === 'USER' ? approver.principalId : undefined;

			return {
				appearance: 'circle',
				src: approver.avatarUrl || '',
				name: approver.name || '',
				size: 'small',
				status,
				userId,
			};
		});
	}, [approvers]);

	const { responseCount, hasDenials } = useMemo(() => {
		let approvedCount = 0;
		let deniedCount = 0;
		for (const a of approvers ?? []) {
			if (a.status === 'REVIEWER_APPROVED') {
				approvedCount++;
			} else if (a.status === 'REVIEWER_DENIED') {
				deniedCount++;
			}
		}
		return {
			responseCount: approvedCount + deniedCount,
			hasDenials: deniedCount > 0,
		};
	}, [approvers]);

	// Cross-mode approval request: the current user's approval is needed on the other version
	// (draft vs published) of this page.
	const [dismissedCrossModeReviewId, setDismissedCrossModeReviewId] = useState<string | null>(null);
	const { push } = useRouteActions();
	const [{ spaceKey }] = usePageState();

	const respondOnOtherMode = useCallback(() => {
		if (contentStatus === 'DRAFT') {
			// In the editor: the request is on the published page
			setCrossModeApprovalRespondIntent({ contentId, contentStatus: 'CURRENT' });
			push(VIEW_PAGE.toUrl({ spaceKey, contentId }));
			return;
		}

		// On the published page: the request is on the draft
		setCrossModeApprovalRespondIntent({ contentId, contentStatus: 'DRAFT' });
		push(EDIT_PAGE_V2.toUrl({ spaceKey, contentType: 'pages', contentId }));
	}, [contentId, contentStatus, push, spaceKey]);

	const dismissCrossModeBanner = useCallback(() => {
		setDismissedCrossModeReviewId(otherModeReview?.reviewId ?? null);
	}, [otherModeReview?.reviewId]);

	// End-state dismissals are persisted per review (shared with the completion spotlight);
	// this state hides the banner immediately.
	const [dismissedEndStateReviewId, setDismissedEndStateReviewId] = useState<string | null>(null);
	const reviewId = approvalRequest?.review?.reviewId;
	const isReviewCompleted =
		approvalRequest?.review?.reviewStatus === 'APPROVED' ||
		approvalRequest?.review?.reviewStatus === 'DENIED';
	// Read storage once per review, and only for the requester of a completed review with the
	// banner updates exp on. No exposure here: it fires where the end-state banner renders.
	const isEndStateDismissedInStorage = useMemo(
		() =>
			isCurrentUserRequestor &&
			isReviewCompleted &&
			!!reviewId &&
			UNSAFE_expValEqualsNoExposure('confluence_approvals_banner_updates_exp', 'isEnabled', true) &&
			getDismissedCompletionReviewIds().includes(reviewId),
		[isCurrentUserRequestor, isReviewCompleted, reviewId],
	);
	const dismissEndStateBanner = useCallback(() => {
		if (!reviewId) return;
		setDismissedEndStateReviewId(reviewId);
		addDismissedCompletionReviewId(reviewId);
	}, [reviewId]);

	const formattedDueDate = useMemo(() => {
		const dueDate = approvalRequest?.review?.dueDate;
		if (!dueDate) {
			return null;
		}

		const date = getNoonUtcDateFromDateOnly(dueDate);
		if (!date) {
			return null;
		}

		return formatDate(date, {
			year: 'numeric',
			month: 'short',
			day: 'numeric',
			timeZone: 'UTC',
		});
	}, [approvalRequest?.review?.dueDate, formatDate]);

	// Don't render for non-premium editions, when space-level approvals are disabled,
	// or if loading, no review, or review is not pending
	if (edition !== ConfluenceEdition.PREMIUM) return null;
	if (!isContentApprovalsEnabled) return null;
	if (approvalRequestLoading) return null;

	// A pending review on this mode takes priority over a request on the other mode.
	if (!approvalRequest?.review || approvalRequest.review.reviewStatus !== 'PENDING') {
		const isCrossModeRequestVisible =
			otherModeReview?.reviewStatus === 'PENDING' &&
			otherModeCurrentUserApprover?.status === 'PENDING_REVIEW' &&
			dismissedCrossModeReviewId !== otherModeReview.reviewId;
		if (
			isCrossModeRequestVisible &&
			expValEquals('confluence_approvals_banner_updates_exp', 'isEnabled', true)
		) {
			// Cross-mode banners show only the request: no progress bar or avatars. Without a
			// message, name the requester so the banner keeps a second line.
			const reviewComment = otherModeReview.reviewComment || '';
			const { requestorName } = otherModeReview;

			return (
				<BannerComponent
					heading={formatMessage(
						contentStatus === 'DRAFT'
							? i18n.yourApprovalNeededOnPublishedPageHeading
							: i18n.yourApprovalNeededOnUnpublishedChangesHeading,
					)}
					description={
						reviewComment
							? formatMessage(i18n.approverRequestDescription, {
									requestorName,
									reviewComment,
								})
							: requestorName
								? formatMessage(i18n.approverRequestFromDescription, { requestorName })
								: undefined
					}
					action={{
						type: 'button',
						label: formatMessage(i18n.respondCta),
						onClick: respondOnOtherMode,
					}}
					dismiss={{
						label: formatMessage(i18n.dismissBannerLabel),
						onClick: dismissCrossModeBanner,
					}}
				/>
			);
		}

		// End-state banner: the requester sees the outcome of this mode's completed review. A
		// cross-mode request (above) takes priority, so the two never show together.
		const review = approvalRequest?.review;
		const isEndStateVisible =
			isCurrentUserRequestor &&
			(review?.reviewStatus === 'APPROVED' || review?.reviewStatus === 'DENIED') &&
			dismissedEndStateReviewId !== review.reviewId &&
			!isEndStateDismissedInStorage;
		if (
			isEndStateVisible &&
			expValEquals('confluence_approvals_banner_updates_exp', 'isEnabled', true)
		) {
			const isApproved = review.reviewStatus === 'APPROVED';
			const heading = formatMessage(
				isApproved ? i18n.pageApprovedHeading : i18n.changesRequestedHeading,
			);

			return (
				<BannerComponent
					heading={heading}
					description={formatMessage(
						hasDenials ? i18n.respondedDescription : i18n.approvedDescription,
						{
							current: responseCount,
							total: approvers?.length ?? 0,
						},
					)}
					icon={
						isApproved ? (
							<CheckCircleIcon label={heading} color={token('color.icon.accent.lime')} />
						) : (
							<StatusWorkflowReturnedIcon
								label={heading}
								color={token('color.icon.accent.yellow')}
							/>
						)
					}
					avatarData={avatarData}
					profileCardMode={getProfileCardMode(avatarData.length > 0)}
					action={{
						type: 'button',
						label: formatMessage(i18n.viewDetailsCta),
						onClick: openApprovalsPane,
					}}
					dismiss={{
						label: formatMessage(i18n.dismissBannerLabel),
						onClick: dismissEndStateBanner,
					}}
					showProgressBar
				/>
			);
		}

		return null;
	}

	const isApproverPendingResponse = currentUserApprover?.status === 'PENDING_REVIEW';
	const isApproverResponded =
		currentUserApprover != null && currentUserApprover.status !== 'PENDING_REVIEW';

	// Description text: "X/Y approved" when no denials, "X/Y responded" when there are denials
	const progressDescription = formatMessage(
		hasDenials ? i18n.respondedDescription : i18n.approvedDescription,
		{
			current: responseCount,
			total: approvers?.length ?? 0,
		},
	);

	if (isPendingReviewBlockedByRequiredApproversChange) {
		if (!isCurrentUserRequestor && !currentUserApprover) return null;

		return (
			<BannerComponent
				heading={formatMessage(i18n.blockedApprovalHeading)}
				icon={
					<StatusWarningIcon
						label={formatMessage(i18n.blockedApprovalHeading)}
						color={token('color.icon.warning')}
					/>
				}
				action={{
					type: 'button',
					label: formatMessage(i18n.seeDetailsCta),
					onClick: openApprovalsPane,
				}}
			/>
		);
	}

	// Approver who hasn't responded
	if (isApproverPendingResponse) {
		const reviewComment = approvalRequest.review.reviewComment || '';
		const requestorName = approvalRequest.review.requestorName || '';
		const hasReviewComment = Boolean(reviewComment);
		let heading: string;
		if (formattedDueDate) {
			heading = formatMessage(i18n.yourApprovalNeededByDueDateHeading, {
				dueDate: formattedDueDate,
			});
		} else if (
			contentStatus === 'DRAFT' &&
			hasReviewComment &&
			expValEquals('confluence_approvals_banner_updates_exp', 'isEnabled', true)
		) {
			// In the editor the review is on the draft. With a message (no progress bar) there is
			// room to say so.
			heading = formatMessage(i18n.yourApprovalNeededOnUnpublishedChangesHeading);
		} else {
			heading = formatMessage(i18n.yourApprovalNeededHeading);
		}

		return (
			<BannerComponent
				heading={heading}
				description={
					hasReviewComment
						? formatMessage(i18n.approverRequestDescription, { requestorName, reviewComment })
						: progressDescription
				}
				avatarData={hasReviewComment ? undefined : avatarData}
				profileCardMode={getProfileCardMode(!hasReviewComment && avatarData.length > 0)}
				action={{
					type: 'button',
					label: formatMessage(i18n.respondCta),
					onClick: openApprovalsPane,
				}}
				showProgressBar={!hasReviewComment}
			/>
		);
	}

	// Approver who has responded, or Requestor/Viewer
	return (
		<BannerComponent
			heading={formatMessage(i18n.approvalInProgressHeading)}
			description={progressDescription}
			avatarData={avatarData}
			profileCardMode={getProfileCardMode(avatarData.length > 0)}
			action={{
				type: 'button',
				label: formatMessage(isApproverResponded ? i18n.editResponseCta : i18n.viewDetailsCta),
				onClick: openApprovalsPane,
			}}
			showProgressBar
		/>
	);
};
